import React from 'react';
import { createPortal } from 'react-dom';
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import toast from 'react-hot-toast';

export default function ProfileModals({ 
    selectedProfile, setSelectedProfile, 
    completedClasses, allHomeworks, allClasses,
    reportProfile, setReportProfile,
    spectatingClass, setSpectatingClass 
}) {
    if (!selectedProfile && !reportProfile && !spectatingClass) return null;

    // 🔥 FUNCTION TO GENERATE & DOWNLOAD PDF
    const handleDownloadPDF = async () => {
        const element = document.getElementById('pdf-report-container');
        if (!element) return;

        const loading = toast.loading("Generating Premium Report...");
        try {
            const scrollBoxes = element.querySelectorAll('.scroll-box');
            scrollBoxes.forEach(box => {
                box.dataset.originalMaxHeight = box.style.maxHeight;
                box.dataset.originalOverflow = box.style.overflowY;
                box.style.maxHeight = 'none';
                box.style.overflowY = 'visible';
            });

            const canvas = await html2canvas(element, { 
                scale: 2, 
                backgroundColor: 'var(--bg-primary)', 
                useCORS: true 
            });

            scrollBoxes.forEach(box => {
                box.style.maxHeight = box.dataset.originalMaxHeight;
                box.style.overflowY = box.dataset.originalOverflow;
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`${selectedProfile.name}_Academy_Report.pdf`);
            
            toast.success("Report Downloaded Successfully!", { id: loading });
        } catch (error) {
            toast.error("Failed to generate PDF.", { id: loading });
            console.error(error);
        }
    };

    if (selectedProfile) {
        const isStudent = selectedProfile.role === 'STUDENT';

        const attendedClasses = completedClasses.filter(c => 
            (c.students || []).includes(selectedProfile.email) || 
            (c.students || []).some(s => s.email === selectedProfile.email)
        ).sort((a, b) => (b.endedAt || 0) - (a.endedAt || 0));
        
        const myHomeworks = allHomeworks.filter(h => (h.assignedTo || []).includes(selectedProfile.email));
        const completedHws = myHomeworks.filter(h => (h.completedBy || []).includes(selectedProfile.email));

        let totalStars = 0;
        attendedClasses.forEach(c => {
            const safeEmail = selectedProfile.email.replace(/\./g, '_');
            if (c.studentArcadeStats && c.studentArcadeStats[safeEmail]) {
                totalStars += (c.studentArcadeStats[safeEmail].stars || 0);
            }
        });

        // Get stars for the last 5 classes for the sparkline chart
        const last5Classes = [...attendedClasses].slice(0, 5).reverse();
        const sparkPoints = last5Classes.map((c, idx) => {
            const safeEmail = selectedProfile.email.replace(/\./g, '_');
            const stars = c.studentArcadeStats?.[safeEmail]?.stars || 0;
            return { x: idx * 75 + 30, y: 70 - (stars * 10), stars };
        });
        
        const pathD = sparkPoints.length > 0 
            ? `M ${sparkPoints.map(p => `${p.x} ${p.y}`).join(' L ')}` 
            : '';

        const academyName = "Hayaa Chess Academy";

        return createPortal(
            <div 
                onClick={() => setSelectedProfile(null)}
                style={{
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    width: '100vw', 
                    height: '100vh', 
                    background: 'rgba(15, 23, 42, 0.65)', 
                    backdropFilter: 'blur(8px)',
                    zIndex: 999999, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    padding: '20px', 
                    boxSizing: 'border-box'
                }}
            >
                <style>{`
                    @keyframes sleekPop {
                        from { transform: scale(0.95) translateY(25px); opacity: 0; }
                        to { transform: scale(1) translateY(0); opacity: 1; }
                    }
                    @keyframes float-cute {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-6px); }
                    }
                `}</style>
                <div 
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: 'var(--bg-primary)', 
                        width: '100%', 
                        maxWidth: '950px', 
                        maxHeight: '92vh', 
                        borderRadius: '24px', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        overflow: 'hidden', 
                        boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
                        border: '3px solid rgba(255, 255, 255, 0.95)',
                        position: 'relative',
                        animation: 'sleekPop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
                    }}
                >
                    
                    {/* MODAL HEADER WITH ACTION BUTTONS */}
                    <div style={{
                        padding: '16px 28px', 
                        background: 'var(--bg-primary)', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        borderBottom: '1px solid rgba(163, 177, 198, 0.3)',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                    }}>
                        <div style={{display: 'flex', gap: '10px' }}>
                            {isStudent && (
                                <button 
                                    onClick={handleDownloadPDF} 
                                    style={{
                                        background: '#10b981', 
                                        color: '#ffffff', 
                                        border: 'none', 
                                        padding: '10px 20px', 
                                        borderRadius: '14px', 
                                        cursor: 'pointer', 
                                        fontWeight: '900', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px', 
                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                                        fontSize: '13px',
                                        transition: '0.2s'
                                    }}
                                    onMouseEnter={e => e.target.style.transform = 'scale(1.03)'}
                                    onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    Download PDF Report
                                </button>
                            )}
                        </div>
                        <button 
                            onClick={() => setSelectedProfile(null)} 
                            style={{
                                background: 'transparent', 
                                color: '#ef4444', 
                                border: '2px solid #ef4444', 
                                padding: '8px 18px', 
                                borderRadius: '14px', 
                                cursor: 'pointer', 
                                fontWeight: '800',
                                fontSize: '13px',
                                transition: '0.2s'
                            }}
                            onMouseEnter={e => { e.target.style.background = '#ef4444'; e.target.style.color = '#fff'; }}
                            onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = '#ef4444'; }}
                        >
                            ✖ Close
                        </button>
                    </div>

                    {/* MODAL BODY (This wrapper is what gets converted to PDF) */}
                    <div id="pdf-report-container" style={{
                        background: 'var(--bg-primary)', 
                        padding: '30px', 
                        overflowY: 'auto', 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '25px',
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                        
                        {/* ACADEMY LOGO/HEADER FOR PDF */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '3px double rgba(163, 177, 198, 0.4)', 
                            paddingBottom: '20px', 
                            marginBottom: '5px'
                        }}>
                            <div>
                                <h1 style={{margin: '0 0 4px 0', color: '#2563eb', fontSize: '26px', fontWeight: '900', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M4 22h16" /><path d="M4 20v-3a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v3" /><path d="M6 14V4h12v10" /><path d="M6 4V2h3v2h6V2h3v2" /></svg>
                                    {academyName}
                                </h1>
                                <div style={{color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Official Student Progress Report</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Report Generated:</div>
                                <div style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: '800' }}>{new Date().toLocaleDateString()}</div>
                            </div>
                        </div>

                        {isStudent ? (
                            /* TWO-COLUMN GRID */
                            <div style={{ display: 'flex', gap: '25px', flexWrap: 'wrap' }}>
                                
                                {/* LEFT SIDEBAR: PROFILE CARD & ACHIEVEMENTS */}
                                <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    
                                    {/* STUDENT PROFILE CARD */}
                                    <div className="glass-panel" style={{ 
                                        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', 
                                        padding: '24px', 
                                        borderRadius: '24px', 
                                        color: '#ffffff',
                                        boxShadow: '0 12px 25px rgba(37, 99, 235, 0.22)',
                                        border: '3px solid rgba(255, 255, 255, 0.95)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        position: 'relative'
                                    }}>
                                        <div style={{ 
                                            width: '80px', 
                                            height: '80px', 
                                            borderRadius: '50%', 
                                            background: 'rgba(255, 255, 255, 0.2)', 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            alignItems: 'center', 
                                            fontSize: '44px',
                                            border: '4px solid #ffffff',
                                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
                                            marginBottom: '15px',
                                            animation: 'float-cute 4s ease-in-out infinite'
                                        }}>
                                            {selectedProfile.avatar || '🎓'}
                                        </div>
                                        
                                        <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: '900', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                                            {selectedProfile.name}
                                        </h2>
                                        <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: '600', wordBreak: 'break-all', marginBottom: '15px' }}>
                                            {selectedProfile.email}
                                        </div>

                                        {/* INFO BADGES */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                            <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '12px', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                                                <span style={{ fontWeight: '600' }}>Assigned Coach</span>
                                                <span style={{ fontWeight: '800' }}>{selectedProfile.assignedCoach || 'None'}</span>
                                            </div>
                                            <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '12px', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                                                <span style={{ fontWeight: '600' }}>WhatsApp</span>
                                                <span style={{ fontWeight: '800' }}>{selectedProfile.phone || 'None'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* UNLOCKED ACHIEVEMENTS */}
                                    <div className="clay-card" style={{ padding: '20px', borderRadius: '24px', background: 'var(--bg-primary)', border: '2px solid rgba(255, 255, 255, 0.95)', boxShadow: 'var(--panel-shadow)' }}>
                                        <h4 style={{ margin: '0 0 12px 0', color: '#1e3a8a', fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Unlocked Badges</h4>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            {attendedClasses.length > 0 && (
                                                <span style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#2563eb', border: '1px solid rgba(59, 130, 246, 0.2)', padding: '5px 10px', borderRadius: '30px', fontSize: '10px', fontWeight: 'bold' }}>
                                                    🛡️ Tactics Scholar
                                                </span>
                                            )}
                                            {totalStars > 10 && (
                                                <span style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.2)', padding: '5px 10px', borderRadius: '30px', fontSize: '10px', fontWeight: 'bold' }}>
                                                    🧙‍♂️ Puzzle Wizard
                                                </span>
                                            )}
                                            {completedHws.length === myHomeworks.length && myHomeworks.length > 0 && (
                                                <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)', padding: '5px 10px', borderRadius: '30px', fontSize: '10px', fontWeight: 'bold' }}>
                                                    🏆 Homework Hero
                                                </span>
                                            )}
                                            {selectedProfile.level > 2 && (
                                                <span style={{ background: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed', border: '1px solid rgba(124, 58, 237, 0.2)', padding: '5px 10px', borderRadius: '30px', fontSize: '10px', fontWeight: 'bold' }}>
                                                    🚀 Speedy Knight
                                                </span>
                                            )}
                                            {attendedClasses.length === 0 && totalStars === 0 && (
                                                <span style={{ color: 'var(--text-secondary)', fontSize: '11px', fontStyle: 'italic' }}>Complete tasks to unlock badges!</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* CONTACT/ACTIONS SECTION */}
                                    {selectedProfile.phone && (
                                        <button 
                                            onClick={() => {
                                                const cleanContact = selectedProfile.phone.replace(/[^0-9]/g, '');
                                                window.open(`https://wa.me/${cleanContact}?text=Hello%20${encodeURIComponent(selectedProfile.name)},%20here%20is%20your%20progress%20report%20update!`, '_blank');
                                            }}
                                            style={{
                                                background: '#10b981',
                                                color: '#ffffff',
                                                border: 'none',
                                                padding: '12px',
                                                borderRadius: '16px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                                                transition: '0.2s',
                                                fontSize: '13px'
                                            }}
                                            onMouseEnter={e => e.target.style.transform = 'scale(1.02)'}
                                            onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                                            Send Update via WhatsApp
                                        </button>
                                    )}

                                </div>

                                {/* RIGHT CONTENT: DETAILED GRIDS */}
                                <div style={{ flex: '2 1 400px', display: 'flex', flexDirection: 'column', gap: '22px' }}>
                                    
                                    {/* 4 QUICK STATS ROW */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '15px' }}>
                                        <div className="clay-card" style={{ background: 'var(--bg-primary)', padding: '15px', borderRadius: '18px', textAlign: 'center', border: '1.5px solid rgba(255, 255, 255, 0.9)', boxShadow: 'var(--panel-shadow)' }}>
                                            <div style={{ fontSize: '24px', color: '#10b981', fontWeight: '900' }}>{attendedClasses.length}</div>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold', marginTop: '2px' }}>Attended Classes</div>
                                        </div>
                                        <div className="clay-card" style={{ background: 'var(--bg-primary)', padding: '15px', borderRadius: '18px', textAlign: 'center', border: '1.5px solid rgba(255, 255, 255, 0.9)', boxShadow: 'var(--panel-shadow)' }}>
                                            <div style={{ fontSize: '24px', color: '#f59e0b', fontWeight: '900' }}>{completedHws.length}/{myHomeworks.length}</div>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold', marginTop: '2px' }}>Homeworks Done</div>
                                        </div>
                                        <div className="clay-card" style={{ background: 'var(--bg-primary)', padding: '15px', borderRadius: '18px', textAlign: 'center', border: '1.5px solid rgba(255, 255, 255, 0.9)', boxShadow: 'var(--panel-shadow)' }}>
                                            <div style={{ fontSize: '24px', color: '#3b82f6', fontWeight: '900' }}>⭐ {totalStars}</div>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold', marginTop: '2px' }}>Total Stars</div>
                                        </div>
                                        <div className="clay-card" style={{ background: 'var(--bg-primary)', padding: '15px', borderRadius: '18px', textAlign: 'center', border: '1.5px solid rgba(255, 255, 255, 0.9)', boxShadow: 'var(--panel-shadow)' }}>
                                            <div style={{ fontSize: '24px', color: '#a855f7', fontWeight: '900' }}>Lvl {selectedProfile.level || 1}</div>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold', marginTop: '2px' }}>{selectedProfile.totalXp || 0} Total XP</div>
                                        </div>
                                    </div>

                                    {/* STARS PROGRESS TREND (NEW GRAPH FEATURE!) */}
                                    <div className="clay-card" style={{ padding: '18px', borderRadius: '20px', background: 'var(--bg-primary)', border: '1.5px solid rgba(255, 255, 255, 0.9)', boxShadow: 'var(--panel-shadow)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h4 style={{ margin: 0, color: '#1e3a8a', fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📈 Performance Trend (Stars Progress)</h4>
                                            <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Last 5 Sessions</span>
                                        </div>
                                        {sparkPoints.length === 0 ? (
                                            <div style={{ height: '70px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold' }}>
                                                No recent class stats to trace trend line.
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', margin: '5px 0' }}>
                                                <svg width="400" height="80" viewBox="0 0 400 80" style={{ overflow: 'visible' }}>
                                                    <line x1="20" y1="20" x2="380" y2="20" stroke="rgba(163, 177, 198, 0.15)" strokeWidth="1" strokeDasharray="3" />
                                                    <line x1="20" y1="45" x2="380" y2="45" stroke="rgba(163, 177, 198, 0.15)" strokeWidth="1" strokeDasharray="3" />
                                                    <line x1="20" y1="70" x2="380" y2="70" stroke="rgba(163, 177, 198, 0.15)" strokeWidth="1" strokeDasharray="3" />
                                                    
                                                    <path
                                                        d={pathD}
                                                        fill="none"
                                                        stroke="url(#sparkGrad)"
                                                        strokeWidth="4"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    
                                                    <defs>
                                                        <linearGradient id="sparkGrad" x1="0" y1="0" x2="1" y2="0">
                                                            <stop offset="0%" stopColor="#3b82f6" />
                                                            <stop offset="100%" stopColor="#10b981" />
                                                        </linearGradient>
                                                    </defs>
                                                    
                                                    {sparkPoints.map((p, idx) => (
                                                        <g key={idx}>
                                                            <circle
                                                                cx={p.x}
                                                                cy={p.y}
                                                                r="6"
                                                                fill="#ffffff"
                                                                stroke="#3b82f6"
                                                                strokeWidth="3"
                                                                style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}
                                                            />
                                                            <text
                                                                x={p.x}
                                                                y={p.y - 12}
                                                                textAnchor="middle"
                                                                fontSize="10"
                                                                fontWeight="900"
                                                                fill="var(--text-primary)"
                                                            >
                                                                ⭐{p.stars}
                                                            </text>
                                                        </g>
                                                    ))}
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    {/* CLASS PERFORMANCE LOG */}
                                    <div>
                                        <h3 style={{ color: '#1e3a8a', margin: '0 0 10px 0', fontSize: '15px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                            Class Performance Logs
                                        </h3>
                                        <div className="scroll-box" style={{ background: 'var(--bg-primary)', borderRadius: '16px', border: '1px solid rgba(163, 177, 198, 0.3)', boxShadow: 'var(--neu-shadow-inset)', maxHeight: '160px', overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {attendedClasses.length === 0 ? (
                                                <p style={{ color: 'var(--text-secondary)', textAlign: 'center', margin: '20px 0', fontSize: '12px', fontWeight: 'bold' }}>No classes attended yet.</p>
                                            ) : (
                                                attendedClasses.slice(0, 15).map(c => {
                                                    const safeEmail = selectedProfile.email.replace(/\./g, '_');
                                                    const arcadeData = c.studentArcadeStats?.[safeEmail];
                                                    return (
                                                        <div key={c.id} style={{ background: 'var(--bg-primary)', padding: '12px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(255, 255, 255, 0.95)', boxShadow: 'var(--panel-shadow)' }}>
                                                            <div>
                                                                <div style={{ color: 'var(--text-primary)', fontWeight: '800', fontSize: '13px' }}>{c.topic}</div>
                                                                <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold' }}>{new Date(c.endedAt || c.scheduledAt).toLocaleDateString()}</div>
                                                            </div>
                                                            <div style={{ textAlign: 'right' }}>
                                                                {arcadeData ? (
                                                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                                        <span style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '800' }}>⭐ {arcadeData.stars || 0}</span>
                                                                        <span style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', padding: '4px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 'bold' }}>{arcadeData.mistakes || 0} Err</span>
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ color: 'var(--text-secondary)', fontSize: '11px', fontStyle: 'italic', fontWeight: 'bold' }}>Practice Mode</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>

                                    {/* HOMEWORK TRACKER */}
                                    <div>
                                        <h3 style={{ color: '#1e3a8a', margin: '0 0 10px 0', fontSize: '15px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                            Homework Quests Tracker
                                        </h3>
                                        <div className="scroll-box" style={{ background: 'var(--bg-primary)', borderRadius: '16px', border: '1px solid rgba(163, 177, 198, 0.3)', boxShadow: 'var(--neu-shadow-inset)', maxHeight: '160px', overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {myHomeworks.length === 0 ? (
                                                <p style={{ color: 'var(--text-secondary)', textAlign: 'center', margin: '20px 0', fontSize: '12px', fontWeight: 'bold' }}>No homework assigned yet.</p>
                                            ) : (
                                                myHomeworks.map(h => {
                                                    const isDone = (h.completedBy || []).includes(selectedProfile.email);
                                                    return (
                                                        <div key={h.id} style={{ background: 'var(--bg-primary)', padding: '12px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(255, 255, 255, 0.95)', boxShadow: 'var(--panel-shadow)' }}>
                                                            <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '800' }}>{h.title}</span>
                                                            <span style={{ 
                                                                background: isDone ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                                                                color: isDone ? '#10b981' : '#ef4444', 
                                                                padding: '4px 10px', 
                                                                borderRadius: '8px', 
                                                                fontSize: '11px', 
                                                                fontWeight: '900',
                                                                border: isDone ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(239, 68, 68, 0.2)'
                                                            }}>
                                                                {isDone ? 'Completed' : 'Pending'}
                                                            </span>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        ) : (
                            /* COACH EXCLUSION CARD */
                            <div style={{textAlign: 'center', color: 'var(--text-secondary)', padding: '60px 40px' }}>
                                <div style={{fontSize: '54px', marginBottom: '15px' }}>👨‍🏫</div>
                                <h3 style={{ fontSize: '20px', fontWeight: '900', color: 'var(--text-primary)' }}>Coach Profile Badge</h3>
                                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Detailed progress and stars metrics are only generated for student profiles.</p>
                            </div>
                        )}
                        
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    return null;
}
